import React, { useState } from 'react';
import useAuthStore from '../../../core/stores/authStore';
import { usePaypointKpi, usePaypointUsages } from '../../hooks/point/usePayPoint';
import { fmt, mdhm, ymd, Trend, KpiGrid, Pager, rangeText } from './PointShared';
import styles from './PointV2.module.css';

/* 팜페이포인트 현황 — Figma 409:21966 · API v2 H-4 (10/7 배포)
   KPI: GET /settlement/paypoint-kpi · 사용 내역: GET /settlement/point-transactions?issuer_type=hq&transaction_type=USE (이번 달) */

const PAGE_SIZE = 20;

// 값이 없으면 '-' (전월이 0 이면 증감 null, 누적 정산은 지급 정책 미정이라 null)
const won = v => (v == null ? '-' : `${fmt(v)}원`);
const trend = v => (v == null ? null : <Trend value={v} suffix="%" />);

const PayPointStatus = () => {
  const pharmacy = useAuthStore(state => state.pharmacy);
  const now = new Date();
  const month = ymd(now).slice(0, 7);
  const start = `${month}-01`;
  const end = ymd(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  const [currentPage, setCurrentPage] = useState(1);
  const { data: kpi } = usePaypointKpi(month, !!pharmacy?.id);
  const { data: usages } = usePaypointUsages(pharmacy?.id, start, end, currentPage, PAGE_SIZE);
  const rows = usages?.items ?? [];
  const total = Number(usages?.total ?? rows.length);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const kpis = [
    { label: '이번 달 사용액', value: won(kpi?.used_amount), right: trend(kpi?.used_amount_delta_pct) },
    { label: '사용 건수', value: kpi?.use_count == null ? '-' : `${fmt(kpi.use_count)}건`, right: trend(kpi?.use_count_delta_pct) },
    { label: '사용 고객', value: kpi?.unique_users == null ? '-' : `${fmt(kpi.unique_users)}명`, right: <span className={styles.kpiNote}>*중복 제외</span> },
    { label: '누적 정산 (연)', value: won(kpi?.settled_ytd) },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <KpiGrid items={kpis} />

        {/* 우리 약국 사용 내역 409:22096 */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>우리 약국 사용 내역</h2>
          <div className={styles.table}>
            <div className={styles.thead}>
              <div className={styles.cell} style={{ minWidth: 140 }}>고객</div>
              <div className={`${styles.cell} ${styles.center}`} style={{ minWidth: 90, maxWidth: 140 }}>구분</div>
              <div className={styles.cell} style={{ minWidth: 140 }}>사용처</div>
              <div className={`${styles.cell} ${styles.right}`} style={{ minWidth: 140 }}>금액</div>
              <div className={styles.cell} style={{ minWidth: 120, maxWidth: 200 }}>일시</div>
            </div>
            {rows.map((row, i) => (
              <div key={row.id ?? row.transaction_id ?? `${row.created_at}-${i}`} className={styles.tr}>
                {/* user_name = 마스킹 이름 (백엔드 10/22 추가), 비회원이면 null → '-' */}
                <div className={`${styles.cell} ${styles.medium} ${styles.dark}`} style={{ minWidth: 140 }}>{row.user_name ?? '-'}</div>
                <div className={`${styles.cell} ${styles.center}`} style={{ minWidth: 90, maxWidth: 140 }}>
                  <span className={styles.typeTag}>{row.status === 'CANCELED' ? '취소' : '사용'}</span>
                </div>
                {/* 이 약국 사업자번호로 조회한 내역이라 사용처는 항상 우리 약국 */}
                <div className={styles.cell} style={{ minWidth: 140 }}>우리 약국</div>
                <div className={`${styles.cell} ${styles.right} ${styles.medium} ${styles.red}`} style={{ minWidth: 140 }}>
                  -{fmt(Math.abs(Number(row.amount) || 0))}원
                </div>
                <div className={styles.cell} style={{ minWidth: 120, maxWidth: 200 }}>{mdhm(row.created_at)}</div>
              </div>
            ))}
          </div>
          <Pager
            info={`총 ${fmt(total)}건 · ${rangeText(currentPage, PAGE_SIZE, total)} 표시`}
            currentPage={currentPage}
            totalPages={totalPages}
            onChange={setCurrentPage}
          />
        </section>
      </div>
    </div>
  );
};

export default PayPointStatus;
